<template>
    <v-card
        class="table-filter-pill-panel"
        :class="{
            'table-filter-pill-panel--daterange': isDateRange,
            'table-filter-pill-panel--combobox': isComboboxText,
        }"
        flat
    >
        <div v-if="isNumberRange" class="pa-3">
            <div
                class="table-filter-pill-panel__header table-filter-pill-panel__header--inline text-body-2 font-weight-medium mb-2"
            >
                {{ filter.label }}
            </div>
            <v-row dense>
                <v-col cols="6">
                    <v-text-field
                        v-model="pickerMin"
                        type="text"
                        inputmode="decimal"
                        dense
                        outlined
                        hide-details
                        autofocus
                        :label="$translate('numberRangeMin')"
                        :suffix="filter.unit"
                    />
                </v-col>
                <v-col cols="6">
                    <v-text-field
                        v-model="pickerMax"
                        type="text"
                        inputmode="decimal"
                        dense
                        outlined
                        hide-details
                        :label="$translate('numberRangeMax')"
                        :suffix="filter.unit"
                    />
                </v-col>
            </v-row>
            <v-card-actions class="px-0 pb-0 pt-2">
                <v-spacer />
                <v-btn small text @click="$emit('close')">
                    {{ $translate("dateRangeClose") }}
                </v-btn>
                <v-btn
                    color="primary"
                    small
                    text
                    :disabled="!hasNumberRangeInput"
                    @click="applyNumberRange"
                >
                    {{ $translate("dateRangeApply") }}
                </v-btn>
            </v-card-actions>
        </div>

        <div v-else-if="isSlider" class="pa-3 table-filter-pill-panel--slider">
            <div
                class="table-filter-pill-panel__header table-filter-pill-panel__header--inline d-flex align-center justify-space-between text-body-2 font-weight-medium mb-2"
            >
                <span>{{ filter.label }}</span>
                <span class="table-filter-pill-panel__slider-value">
                    {{ sliderDisplayValue }}
                </span>
            </div>
            <v-slider
                v-model="sliderValue"
                :min="sliderMin"
                :max="sliderMax"
                :step="sliderStep"
                hide-details
                dense
                thumb-label
            />
            <v-card-actions class="px-0 pb-0 pt-2">
                <v-spacer />
                <v-btn small text @click="$emit('close')">
                    {{ $translate("dateRangeClose") }}
                </v-btn>
                <v-btn
                    color="primary"
                    small
                    text
                    @click="applySlider"
                >
                    {{ $translate("dateRangeApply") }}
                </v-btn>
            </v-card-actions>
        </div>

        <template v-else-if="isDateRange">
            <div
                class="table-filter-pill-panel__header px-3 py-2 text-body-2 font-weight-medium"
            >
                {{ filter.label }}
            </div>
            <div
                v-if="showDateRangePresets"
                class="table-filter-pill-panel__presets px-2 pt-2"
            >
                <v-btn
                    v-for="code in datePresetChoices"
                    :key="code"
                    x-small
                    text
                    :class="[
                        'text-none table-filter-pill-panel__preset',
                        {
                            'table-filter-pill-panel__preset--active':
                                selectedPreset === code,
                        },
                    ]"
                    @click="selectPreset(code)"
                >
                    {{ datePresetLabel(code) }}
                </v-btn>
            </div>
            <v-date-picker
                v-model="pickerRange"
                range
                color="primary"
                scrollable
                no-title
                :first-day-of-week="1"
            />
            <v-divider />
            <v-card-actions class="px-3 py-2">
                <v-spacer />
                <v-btn small text @click="$emit('close')">
                    {{ $translate("dateRangeClose") }}
                </v-btn>
                <v-btn
                    color="primary"
                    small
                    text
                    :disabled="!isRangeComplete"
                    @click="applyDateRange"
                >
                    {{ $translate("dateRangeApply") }}
                </v-btn>
            </v-card-actions>
        </template>

        <div v-else-if="isDateTime" class="pa-2">
            <div
                class="table-filter-pill-panel__header table-filter-pill-panel__header--inline px-1 py-2 text-body-2 font-weight-medium"
            >
                {{ filter.label }}
            </div>
            <DateTime
                :fixed="true"
                :clearable="filter.clearable != false"
                :dense="true"
                :disabled="filter.disabled"
                :label="filter.label"
                :min-date="null"
                :value="value"
                hide-details
                @input="onDateTimeInput"
            />
        </div>

        <template v-else-if="isComboboxText">
            <div class="px-3 pt-3">
                <v-text-field
                    :value="textValue"
                    :label="comboboxLabel"
                    :type="filter.type === 'float' || filter.type === 'integer' ? 'number' : 'text'"
                    dense
                    hide-details
                    outlined
                    autofocus
                    clearable
                    :loading="loading"
                    @input="onTextInput"
                    @keydown.enter.prevent="applyTextInput"
                />
            </div>

            <v-progress-linear v-if="loading" indeterminate />

            <div
                v-else-if="comboboxListChoices.length"
                class="table-filter-pill-panel__list-wrap"
            >
                <v-list dense class="table-filter-pill-panel__list py-0">
                    <v-list-item
                        v-for="(choice, index) in comboboxListChoices"
                        :key="'combobox-' + String(choice.value) + '-' + index"
                        class="table-filter-pill-panel__item"
                        @click="onComboboxChoiceClick(choice)"
                    >
                        <v-list-item-content>
                            <CatalogChoiceLabel
                                v-if="isCatalogChoiceDisplay(choice)"
                                :seller-name="choice.sellerName"
                                :catalog-name="choice.catalogName"
                                :seller-connected="choice.sellerConnected"
                            />
                            <v-list-item-title
                                v-else
                                class="table-filter-pill-panel__choice-title"
                            >
                                <MiddleEllipsisText :text="choice.text" />
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </div>
        </template>

        <div v-else-if="isPlainTextInput" class="pa-3">
            <v-text-field
                :value="textValue"
                :label="filter.label"
                :type="filter.type === 'float' || filter.type === 'integer' ? 'number' : 'text'"
                dense
                hide-details
                outlined
                autofocus
                clearable
                @input="onTextInput"
                @keydown.enter.prevent="applyTextInput"
            />
        </div>

        <template v-else-if="isPanelList">
            <div
                v-if="filter.multiple"
                class="table-filter-pill-panel__header px-3 py-2 text-body-2 font-weight-medium"
            >
                {{ filter.label }}
            </div>

            <div v-if="showSearch" class="px-2 pt-2 pb-1">
                <v-text-field
                    v-model="searchQuery"
                    dense
                    hide-details
                    solo
                    flat
                    background-color="transparent"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="searchPlaceholder"
                    autofocus
                    clearable
                    @input="onSearchInput"
                />
            </div>

            <v-progress-linear v-if="loading" indeterminate />

            <div
                v-else
                ref="listWrap"
                class="table-filter-pill-panel__list-wrap"
                @scroll.passive="onListScroll"
            >
                <v-list dense class="table-filter-pill-panel__list py-0">
                    <template v-if="!displayChoices.length">
                        <v-list-item disabled>
                            <v-list-item-title class="text-caption grey--text">
                                {{ $translate("noResults") }}
                            </v-list-item-title>
                        </v-list-item>
                    </template>
                    <template v-for="(choice, index) in displayChoices">
                        <v-subheader
                            v-if="choice.header"
                            :key="'header-' + index"
                            class="px-3"
                        >
                            {{ choice.header }}
                        </v-subheader>
                        <v-list-item
                            v-else
                            :key="'choice-' + String(choice.value) + '-' + index"
                            class="table-filter-pill-panel__item"
                            @click="onChoiceClick(choice)"
                        >
                            <v-list-item-action
                                v-if="filter.multiple"
                                class="mr-2 my-0 align-self-center"
                            >
                                <v-checkbox
                                    :input-value="isSelected(choice.value)"
                                    hide-details
                                    dense
                                    class="mt-0 pt-0"
                                    @click.stop="toggleChoice(choice)"
                                />
                            </v-list-item-action>
                            <v-list-item-content>
                                <CatalogChoiceLabel
                                    v-if="isCatalogChoiceDisplay(choice)"
                                    :seller-name="choice.sellerName"
                                    :catalog-name="choice.catalogName"
                                    :seller-connected="choice.sellerConnected"
                                />
                                <v-list-item-title
                                    v-else
                                    class="table-filter-pill-panel__choice-title"
                                >
                                    <MiddleEllipsisText :text="choice.text" />
                                </v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action
                                v-if="!filter.multiple && isSelected(choice.value)"
                                class="my-0"
                            >
                                <v-icon small color="primary">mdi-check</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </template>
                    <v-list-item v-if="loadingMore" disabled>
                        <v-list-item-content>
                            <v-progress-linear indeterminate />
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </div>

            <template v-if="filter.multiple && visibleSelectableChoices.length">
                <v-divider />
                <v-list dense class="py-0">
                    <v-list-item class="table-filter-pill-panel__item" @click="toggleSelectAll">
                        <v-list-item-action class="mr-2 my-0 align-self-center">
                            <v-checkbox
                                :input-value="allSelected"
                                :indeterminate="someSelected && !allSelected"
                                hide-details
                                dense
                                class="mt-0 pt-0"
                            />
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>{{ selectAllLabel }}</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </template>
        </template>
    </v-card>
</template>

<script>
import debounce from "debounce";
import GlobalMixins from "@/mixins/global";
import DateTime from "@/components/common/datetime";
import CatalogChoiceLabel from "@/components/common/catalog-choice-label.vue";
import MiddleEllipsisText from "@/components/common/middle-ellipsis-text.vue";
import {
    DATE_RANGE_PRESETS,
    activeDateRangePreset,
    dateRangePresetLabel,
    resolveDateRangePreset,
} from "@/utils/dateRangePresets";
import {
    catalogChoiceMatchesQuery,
    createChoiceContext,
    fetchFilterChoicesPage,
    groupCatalogChoices,
    flattenSelectableChoices,
    hasFilterChoiceDisplayLabel,
    isCatalogChoiceDisplay as isCatalogChoiceRow,
    isDateRangeFilter,
    isComboboxTextFilter,
    isDateTimeFilter,
    isNumberRangeFilter,
    isPanelListFilter,
    isSliderFilter,
    filterChoiceMatchesQuery,
    isRemoteFilter,
    isTextInputFilter,
    mergeFilterChoicePages,
    normalizeFilterStoredValue,
    resolveStaticChoices,
    shouldPrefetchRemoteChoices,
    shouldShowSearch,
    supportsRemoteChoicePaging,
    supportsRemoteSearch,
} from "@/utils/filterChoices";

export default {
    name: "TableFilterPillPanel",
    components: { CatalogChoiceLabel, DateTime, MiddleEllipsisText },
    mixins: [GlobalMixins],
    props: {
        filter: {
            type: Object,
            required: true,
        },
        value: {
            required: false,
            default: null,
        },
        filters: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            choices: [],
            loading: false,
            loadingMore: false,
            choicesHasMore: false,
            choicesNextOffset: 0,
            choicesRequestId: 0,
            searchQuery: "",
            textValue: this.value ?? "",
            pickerRange: [],
            pickerMin: "",
            pickerMax: "",
            sliderValue: 0,
            selectedPreset: null,
            suppressPickerEmit: false,
        };
    },
    computed: {
        isComboboxText() {
            return isComboboxTextFilter(this.filter);
        },
        isPlainTextInput() {
            return isTextInputFilter(this.filter) && !this.isComboboxText;
        },
        isSlider() {
            return isSliderFilter(this.filter);
        },
        sliderMin() {
            const n = Number(this.filter.min);
            return Number.isFinite(n) ? n : 0;
        },
        sliderMax() {
            const n = Number(this.filter.max);
            return Number.isFinite(n) ? n : 100;
        },
        sliderStep() {
            const n = Number(this.filter.step);
            return Number.isFinite(n) && n > 0 ? n : 1;
        },
        sliderDisplayValue() {
            const n = Number(this.sliderValue);
            const value = Number.isFinite(n) ? Math.round(n) : 0;
            const unit = this.filter.unit || "";
            return `${value}${unit}`;
        },
        comboboxLabel() {
            if (this.filter.unit) {
                return `${this.filter.label} (${this.filter.unit})`;
            }
            return this.filter.label;
        },
        comboboxListChoices() {
            const items = flattenSelectableChoices(this.choices);
            const query = String(this.textValue || "").trim().toLowerCase();
            if (!query) {
                return items;
            }
            return items.filter((choice) => {
                if (isCatalogChoiceRow(this.filter, choice)) {
                    return catalogChoiceMatchesQuery(choice, query);
                }
                return String(choice.text || choice.value)
                    .toLowerCase()
                    .includes(query);
            });
        },
        isNumberRange() {
            return isNumberRangeFilter(this.filter);
        },
        isDateRange() {
            return isDateRangeFilter(this.filter);
        },
        showDateRangePresets() {
            return !!this.filter.dateRangePresets;
        },
        datePresetChoices() {
            return this.filter.datePresetChoices && this.filter.datePresetChoices.length
                ? this.filter.datePresetChoices
                : DATE_RANGE_PRESETS;
        },
        isDateTime() {
            return isDateTimeFilter(this.filter);
        },
        isPanelList() {
            return isPanelListFilter(this.filter);
        },
        selectableChoices() {
            return flattenSelectableChoices(this.choices);
        },
        displayChoices() {
            let items = (this.choices || []).filter((item) => {
                if (item.header) {
                    return true;
                }
                if (item.divider) {
                    return false;
                }
                return (
                    item.value !== null &&
                    item.value !== undefined &&
                    hasFilterChoiceDisplayLabel(this.filter, item)
                );
            });
            if (
                this.showSearch &&
                this.searchQuery &&
                !supportsRemoteSearch(this.filter)
            ) {
                items = items.filter((item) => {
                    if (item.header) {
                        return true;
                    }
                    return filterChoiceMatchesQuery(
                        this.filter,
                        item,
                        this.searchQuery,
                    );
                });
            }
            return items;
        },
        /** Choices currently shown in the list (respects local search filter). */
        visibleSelectableChoices() {
            return flattenSelectableChoices(this.displayChoices);
        },
        showSearch() {
            return shouldShowSearch(this.filter, this.selectableChoices.length);
        },
        searchPlaceholder() {
            if (this.filter.searchPlaceholder || this.filter.placeholder) {
                return this.filter.searchPlaceholder || this.filter.placeholder;
            }
            if (this.filter.multiple) {
                return this.$translate("search");
            }
            return this.filter.label;
        },
        selectAllLabel() {
            const visibleCount = this.visibleSelectableChoices.length;
            const hasActiveSearch = !!(
                this.searchQuery && String(this.searchQuery).trim()
            );
            if (
                hasActiveSearch &&
                visibleCount > 0 &&
                visibleCount < this.selectableChoices.length
            ) {
                return this.$translate("selectVisible", { count: visibleCount });
            }
            return this.$translate("selectAll");
        },
        selectedValues() {
            if (this.filter.multiple) {
                const value = this.value;
                if (value === null || value === undefined || value === "") {
                    return [];
                }
                if (Array.isArray(value)) {
                    return value
                        .map((item) =>
                            normalizeFilterStoredValue(item, this.filter),
                        )
                        .filter(
                            (item) =>
                                item !== null &&
                                item !== undefined &&
                                item !== "",
                        );
                }
                if (typeof value === "string") {
                    return value
                        .split(",")
                        .map((part) => part.trim())
                        .filter(Boolean);
                }
                const normalized = normalizeFilterStoredValue(
                    value,
                    this.filter,
                );
                return normalized !== null &&
                    normalized !== undefined &&
                    normalized !== ""
                    ? [normalized]
                    : [];
            }
            return this.value !== null && this.value !== undefined
                ? [this.value]
                : [];
        },
        allSelected() {
            if (!this.filter.multiple || !this.visibleSelectableChoices.length) {
                return false;
            }
            return this.visibleSelectableChoices.every((choice) =>
                this.isSelected(choice.value),
            );
        },
        someSelected() {
            return this.visibleSelectableChoices.some((choice) =>
                this.isSelected(choice.value),
            );
        },
        hasNumberRangeInput() {
            return (
                this.parseNumberInput(this.pickerMin) !== null ||
                this.parseNumberInput(this.pickerMax) !== null
            );
        },
        isRangeComplete() {
            return (
                Array.isArray(this.pickerRange) &&
                this.pickerRange.length === 2 &&
                this.pickerRange[0] &&
                this.pickerRange[1]
            );
        },
    },
    watch: {
        value(next) {
            if (isTextInputFilter(this.filter)) {
                this.textValue = next ?? "";
            } else if (this.isNumberRange) {
                this.initializeNumberRange(next);
            } else if (this.isSlider) {
                this.initializeSlider(next);
            } else if (this.isDateRange) {
                this.initializeDateRange(next);
            }
        },
        choices() {
            this.$nextTick(() => this.maybeLoadMoreFromList());
        },
        pickerRange: {
            handler(newRange) {
                if (!this.isDateRange) {
                    return;
                }
                if (
                    Array.isArray(newRange) &&
                    newRange.length === 2 &&
                    newRange[0] &&
                    newRange[1] &&
                    newRange[0] > newRange[1]
                ) {
                    this.pickerRange = [newRange[1], newRange[0]];
                    return;
                }
                if (this.suppressPickerEmit) {
                    this.suppressPickerEmit = false;
                    return;
                }
                if (
                    Array.isArray(newRange) &&
                    newRange.length === 2 &&
                    newRange[0] &&
                    newRange[1]
                ) {
                    this.selectedPreset = null;
                    this.applyDateRange(false);
                }
            },
        },
    },
    created() {
        this.debouncedRemoteSearch = debounce(this.loadChoices, 300);
    },
    mounted() {
        this.initPanelState();
    },
    methods: {
        initPanelState() {
            if (this.isNumberRange) {
                this.initializeNumberRange(this.value);
                return;
            }
            if (this.isSlider) {
                this.initializeSlider(this.value);
                return;
            }
            if (this.isDateRange) {
                this.initializeDateRange(this.value);
                return;
            }
            if (this.isPanelList || this.isComboboxText) {
                this.loadChoices();
            }
        },
        initializeSlider(value) {
            const n = Number(value);
            if (!Number.isFinite(n) || n <= 0) {
                this.sliderValue = this.sliderMin;
                return;
            }
            this.sliderValue = Math.min(
                this.sliderMax,
                Math.max(this.sliderMin, n),
            );
        },
        applySlider() {
            const n = Number(this.sliderValue);
            if (!Number.isFinite(n) || n <= 0) {
                this.$emit("change", null);
                this.$emit("close");
                return;
            }
            this.$emit("change", n);
        },
        choiceContext() {
            return createChoiceContext(this);
        },
        isCatalogChoiceDisplay(choice) {
            return isCatalogChoiceRow(this.filter, choice);
        },
        parseNumberInput(value) {
            if (value === null || value === undefined || value === "") {
                return null;
            }
            const parsed = Number(String(value).replace(",", "."));
            return Number.isFinite(parsed) ? parsed : null;
        },
        initializeNumberRange(rangeValue = this.value) {
            if (!rangeValue || typeof rangeValue !== "object") {
                this.pickerMin = "";
                this.pickerMax = "";
                return;
            }
            this.pickerMin =
                rangeValue.min === null || rangeValue.min === undefined
                    ? ""
                    : String(rangeValue.min);
            this.pickerMax =
                rangeValue.max === null || rangeValue.max === undefined
                    ? ""
                    : String(rangeValue.max);
        },
        initializeDateRange(rangeValue = this.value) {
            const preset = activeDateRangePreset(this.filters, this.filter.field);
            if (preset) {
                this.selectedPreset = preset;
                const resolved = resolveDateRangePreset(preset);
                if (resolved) {
                    this.suppressPickerEmit = true;
                    this.pickerRange = [resolved.min, resolved.max];
                    return;
                }
            }
            this.selectedPreset = null;
            if (!rangeValue || typeof rangeValue !== "object") {
                this.pickerRange = [];
                return;
            }
            const min = rangeValue.min;
            const max = rangeValue.max;
            if (min || max) {
                this.suppressPickerEmit = true;
                this.pickerRange = [min || max, max || min];
            } else {
                this.pickerRange = [];
            }
        },
        datePresetLabel(code) {
            return dateRangePresetLabel(this, code);
        },
        selectPreset(code) {
            this.selectedPreset = code;
            const resolved = resolveDateRangePreset(code);
            if (!resolved) {
                return;
            }
            this.suppressPickerEmit = true;
            this.pickerRange = [resolved.min, resolved.max];
            this.$emit("change", { preset: code });
        },
        applyNumberRange() {
            let min = this.parseNumberInput(this.pickerMin);
            let max = this.parseNumberInput(this.pickerMax);
            if (min === null && max === null) {
                return;
            }
            if (min !== null && max !== null && min > max) {
                [min, max] = [max, min];
            }
            const payload = { min, max };
            const current = this.value || {};
            if (current.min === payload.min && current.max === payload.max) {
                this.$emit("close");
                return;
            }
            this.$emit("change", payload);
        },
        applyDateRange(emitClose = true) {
            if (this.selectedPreset) {
                if (emitClose) {
                    this.$emit("close");
                }
                return;
            }
            if (!this.isRangeComplete) {
                return;
            }
            const [start, end] = this.pickerRange;
            const payload = { min: start, max: end };
            const current = this.value || {};
            if (current.min === payload.min && current.max === payload.max) {
                if (emitClose) {
                    this.$emit("close");
                }
                return;
            }
            this.$emit("change", payload);
        },
        onDateTimeInput(value) {
            this.$emit("change", value || null);
        },
        async loadChoices({ append = false } = {}) {
            if (!this.isPanelList && !this.isComboboxText) {
                return;
            }
            if (append) {
                if (
                    !supportsRemoteChoicePaging(this.filter) ||
                    !this.choicesHasMore ||
                    this.loading ||
                    this.loadingMore
                ) {
                    return;
                }
                this.loadingMore = true;
            } else {
                this.loading = true;
                this.loadingMore = false;
                this.choicesHasMore = false;
                this.choicesNextOffset = 0;
            }
            const requestId = ++this.choicesRequestId;
            const ctx = this.choiceContext();
            try {
                if (isRemoteFilter(this.filter)) {
                    const page = await fetchFilterChoicesPage(this.filter, {
                        search: this.searchQuery || null,
                        ctx,
                        activeFilters: this.filters,
                        offset: append ? this.choicesNextOffset : 0,
                    });
                    if (requestId !== this.choicesRequestId) {
                        return;
                    }
                    this.choices = append
                        ? this.finalizeCatalogChoices(
                              mergeFilterChoicePages(
                                  this.choices,
                                  page.choices,
                                  this.filter,
                                  ctx,
                              ),
                          )
                        : this.finalizeCatalogChoices(page.choices);
                    this.choicesHasMore = page.hasMore;
                    this.choicesNextOffset = page.nextOffset;
                } else if (append) {
                    this.choicesHasMore = false;
                } else if (this.filter.choices) {
                    this.choices = this.finalizeCatalogChoices(
                        resolveStaticChoices(this.filter, null, ctx),
                    );
                    this.choicesHasMore = false;
                } else {
                    this.choices = [];
                    this.choicesHasMore = false;
                }
            } catch (error) {
                if (requestId !== this.choicesRequestId) {
                    return;
                }
                console.error("Failed to load filter choices", error);
                if (!append) {
                    this.choices = [];
                    this.choicesHasMore = false;
                }
            } finally {
                if (requestId === this.choicesRequestId) {
                    this.loading = false;
                    this.loadingMore = false;
                }
            }
            if (requestId === this.choicesRequestId) {
                this.$nextTick(() => this.maybeLoadMoreFromList());
            }
        },
        onListScroll(event) {
            this.maybeLoadMoreFromElement(event?.target);
        },
        maybeLoadMoreFromList() {
            this.maybeLoadMoreFromElement(this.$refs.listWrap);
        },
        maybeLoadMoreFromElement(el) {
            if (!el || !supportsRemoteChoicePaging(this.filter)) {
                return;
            }
            if (!this.choicesHasMore || this.loading || this.loadingMore) {
                return;
            }
            if (
                shouldPrefetchRemoteChoices({
                    scrollTop: el.scrollTop,
                    clientHeight: el.clientHeight,
                    scrollHeight: el.scrollHeight,
                })
            ) {
                this.loadChoices({ append: true });
            }
        },
        finalizeCatalogChoices(choices) {
            if (this.filter?.source !== "catalog" || !choices?.length) {
                return choices;
            }
            if (choices.some((item) => item.header)) {
                return choices;
            }
            if (typeof this.filter.transform === "function") {
                return this.filter.transform(choices);
            }
            return groupCatalogChoices(choices, {
                myCatalogsLabel: this.$translate("filters.myCatalogs"),
                agencyCatalogsLabel: this.$translate("filters.agencyCatalogs"),
            });
        },
        onComboboxChoiceClick(choice) {
            this.textValue = choice.value;
            this.$emit("change", {
                value: normalizeFilterStoredValue(choice.value, this.filter),
                label: choice.text,
            });
        },
        onSearchInput() {
            if (!supportsRemoteSearch(this.filter)) {
                return;
            }
            this.debouncedRemoteSearch();
        },
        isSelected(choiceValue) {
            if (this.filter.multiple) {
                return this.selectedValues.some(
                    (value) => String(value) === String(choiceValue),
                );
            }
            return String(this.value) === String(choiceValue);
        },
        onChoiceClick(choice) {
            if (choice.header || choice.divider) {
                return;
            }
            if (this.filter.multiple) {
                this.toggleChoice(choice);
                return;
            }
            this.$emit("change", {
                value: normalizeFilterStoredValue(choice.value, this.filter),
                label: choice.text,
            });
        },
        toggleChoice(choice) {
            if (!this.filter.multiple) {
                return;
            }
            const current = [...this.selectedValues];
            const index = current.findIndex(
                (value) => String(value) === String(choice.value),
            );
            if (index >= 0) {
                current.splice(index, 1);
            } else {
                current.push(choice.value);
            }
            this.$emit("change", current.length ? current : null);
        },
        toggleSelectAll() {
            if (!this.filter.multiple) {
                return;
            }
            const visibleValues = this.visibleSelectableChoices.map(
                (choice) => choice.value,
            );
            if (!visibleValues.length) {
                return;
            }
            if (this.allSelected) {
                const remaining = this.selectedValues.filter(
                    (value) =>
                        !visibleValues.some(
                            (visible) => String(visible) === String(value),
                        ),
                );
                this.$emit("change", remaining.length ? remaining : null);
                return;
            }
            const next = [...this.selectedValues];
            visibleValues.forEach((value) => {
                if (
                    !next.some(
                        (selected) => String(selected) === String(value),
                    )
                ) {
                    next.push(value);
                }
            });
            this.$emit("change", next.length ? next : null);
        },
        onTextInput(value) {
            this.textValue = value ?? "";
            if (this.isComboboxText || this.isPlainTextInput) {
                return;
            }
            const normalized = value === "" ? null : value;
            this.$emit("change", normalized);
        },
        applyTextInput() {
            const rawValue = this.textValue;
            if (rawValue === null || rawValue === undefined || rawValue === "") {
                this.$emit("change", null);
                return;
            }
            if (this.filter.type === "integer") {
                const parsed = Number.parseInt(String(rawValue), 10);
                if (!Number.isFinite(parsed)) {
                    return;
                }
                this.$emit("change", parsed);
                return;
            }
            if (this.filter.type === "float") {
                const parsed = Number.parseFloat(String(rawValue));
                if (!Number.isFinite(parsed)) {
                    return;
                }
                this.$emit("change", parsed);
                return;
            }
            this.$emit("change", rawValue);
        },
    },
    i18n: {
        messages: {
            en: {
                noResults: "No results found",
                selectAll: "Select all",
                selectVisible: "Select %{count} items",
                search: "Search…",
                noUser: "No user",
                agents: "Agents",
                unnamed: "Unnamed",
                inventoryOwnerSuppliers: "Suppliers",
                dateRangeClose: "Close",
                dateRangeApply: "Apply",
                numberRangeMin: "Min",
                numberRangeMax: "Max",
                filters: {
                    myCatalogs: "My catalogs",
                    agencyCatalogs: "Agency catalogs",
                },
            },
            fr: {
                noResults: "Aucun résultat trouvé",
                selectAll: "Tout sélectionner",
                selectVisible: "Sélectionner %{count} éléments",
                search: "Rechercher…",
                noUser: "Aucun utilisateur",
                agents: "Agents",
                unnamed: "Sans nom",
                inventoryOwnerSuppliers: "Fournisseurs",
                dateRangeClose: "Fermer",
                dateRangeApply: "Appliquer",
                numberRangeMin: "Min",
                numberRangeMax: "Max",
                filters: {
                    myCatalogs: "Mes catalogues",
                    agencyCatalogs: "Catalogues en agence",
                },
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.table-filter-pill-panel {
    min-width: 280px;
    max-width: 360px;
    overflow: hidden;
}

.table-filter-pill-panel--daterange {
    max-width: none;
}

.table-filter-pill-panel--combobox {
    overflow: visible;
}

.table-filter-pill-panel__slider-value {
    font-variant-numeric: tabular-nums;
    color: $color-gs-bold;
}

.table-filter-pill-panel__header {
    border-bottom: 1px solid $color-gs-semilight;
    color: $color-gs-extrabold;
}

.table-filter-pill-panel__header--inline {
    border-bottom: none;
}

.table-filter-pill-panel__presets {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
}

.table-filter-pill-panel__preset {
    border: 1px solid $color-gs-semilight;
    border-radius: 999px;
    padding: 0 8px;
    color: $color-gs-bold;
}

.table-filter-pill-panel__preset--active {
    background: $color-primary-extralight;
    border-color: $color-primary;
    color: $color-primary;
}

.table-filter-pill-panel__list-wrap {
    max-height: 280px;
    overflow-y: auto;
}

.table-filter-pill-panel__item {
    cursor: pointer;

    &:hover {
        background: $color-gs-extralight;
    }
}

.table-filter-pill-panel__choice-title {
    // Let MiddleEllipsisText own truncation (middle), not end ellipsis.
    overflow: hidden;
    text-overflow: clip;
    min-width: 0;
}
</style>
